/**
 * One account owed a re-approach.
 *
 * ONE component for both dashboard shapes, so the Command Deck and the tracking tower cannot
 * disagree about who is owed a chase or what may be done about it.
 *
 * DRAFT, NEVER SEND. His decision, and it is enforced upstream of this file rather than by it:
 * `Mail.Send` was deliberately excluded from the Graph grant so MO OS cannot send as him, and an
 * Outlook draft would need `Mail.ReadWrite`, wider than the read-only grant held now. So the most
 * this surface ever does is put text on his clipboard.
 *
 * THE DRAFT ARRIVES ON THE ROW, ALREADY WRITTEN, when the background pass has reached this account
 * (see `mo/chaseDraftPass.ts`). The Ask Zou4Mo button stays for the case where he wants a different
 * one, or wants to steer it, but the point of this work is that he should not have to ask.
 *
 * ABSENT, EMPTY AND REFUSED ARE THREE DIFFERENT THINGS and are rendered as three different things.
 * No `draft` field means the pass has not got here yet. A draft with a `why` and no body means MO
 * OS looked and had nothing specific to say, which is a real answer: he can already get a generic
 * follow-up from anywhere, and one of those dressed up as MO OS's work is worse than none.
 */

import { useState } from 'react';
import type { DashboardChase, DashboardChaseDraft } from '@rbv/shared';
import { Icon } from './Icon.js';
import { dropChase, snoozeChase } from './api.js';
import { openAsk } from './ChatOverlay.js';
import { DeckDetail, type DeckDetailItem } from './DeckDetail.js';

/** A week, in the shape the endpoint wants. */
function inDays(n: number): string {
  return new Date(Date.now() + n * 86_400_000).toISOString();
}

export function ChaseRow({
  chase,
  onDone,
}: {
  chase: DashboardChase;
  /** Called after a snooze or drop lands, so the caller can reload the payload. */
  onDone: () => void;
}): JSX.Element {
  const [dropping, setDropping] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const send = async (p: Promise<{ ok: true } | { ok: false; error: string }>) => {
    setError(null);
    const r = await p;
    if (r.ok) {
      onDone();
      return;
    }
    /*
     * Shown, not swallowed, and `onDone` is NOT called. Reloading on a failed write would redraw
     * the row unchanged and read as the click having done nothing at all, which is the state that
     * leaves him believing an account is snoozed when it is not.
     */
    setError(r.error);
  };

  /*
   * WHAT THE ROW CANNOT SHOW. The drafted email is the reason this lane exists and the row shows
   * it in a clamped block under three buttons, so the thing he most needs to read is the thing
   * hardest to read. Opening the row puts the whole draft, its grounding and its sources in one
   * place, with the same actions the row already offers and nothing new.
   *
   * The state lives HERE rather than in the deck, so the tracking tower gets the same panel
   * without wiring: `ChaseRow` is the one component both surfaces use, which is the whole reason
   * it exists.
   */
  const detail = (): DeckDetailItem => {
    const d = chase.draft;
    const tierWhy =
      chase.tier === 1
        ? 'The next move is yours'
        : chase.tier === 2
          ? 'Nobody here has ever replied'
          : 'They owe you, and have for a while';

    return {
      kind: 'Bounty',
      tone: chase.tier === 1 ? 'act' : 'soon',
      title: chase.account,
      meta: chase.line,
      provenance: `tier ${chase.tier}`,
      facts: [
        { label: 'Why it is here', value: tierWhy },
        { label: 'Silent for', value: `${chase.daysQuiet} days` },
        {
          label: 'Write to',
          value: chase.recipient ?? chase.recipientNote ?? 'nobody on record',
        },
        {
          label: 'Draft grounded in',
          value:
            d?.body == null
              ? ''
              : d.grounding === 'web'
                ? 'a web lookup, since nobody here has ever replied'
                : 'what MO OS has read',
        },
        { label: 'Drafted', value: d?.at?.slice(0, 10) ?? '' },
        {
          label: 'Rests on',
          value: (d?.citations ?? []).map((c) => c.replace(/^.*\//, '').replace(/\.md$/, '')).join(', '),
        },
        { label: 'Sources', value: (d?.sources ?? []).map((x) => x.url).join('  ') },
        {
          label: 'Stage to consider',
          value: d?.proposal == null ? '' : `${d.proposal.stage} — ${d.proposal.why}`,
        },
        ...(d?.caveats ?? []).map((c, i) => ({ label: i === 0 ? 'Caveat' : ' ', value: c })),
      ],
      /*
       * Three different states, rendered as three different things, exactly as the row does.
       * No `draft` at all means the pass has not reached this account, which is not the same
       * fact as having looked and found nothing to say.
       */
      body:
        d === undefined
          ? 'No follow-up has been drafted yet. The drafting pass has not reached this account.'
          : d.body == null
            ? (d.why ?? 'No follow-up was drafted for this account.')
            : `Subject: ${d.subject ?? ''}\n\n${d.body}`,
      actions: [
        ...(d?.body != null
          ? [
              {
                label: 'Copy the email',
                icon: 'action' as const,
                hint: 'Then send it yourself',
                keepOpen: true,
                onClick: () => {
                  // Guarded: `clipboard` is undefined without a secure context, and an unguarded
                  // call throws out of the handler and reads as the button doing nothing.
                  void navigator.clipboard?.writeText(`Subject: ${d.subject ?? ''}\n\n${d.body ?? ''}`);
                },
              },
            ]
          : []),
        {
          label: d?.body != null ? 'Redraft it' : 'Draft one',
          icon: 'action' as const,
          hint: 'Ask Zou for a different angle',
          onClick: () => openAsk({ lane: 'zou', kind: 'follow-up-email', about: chase.account }),
        },
        {
          label: 'Snooze a week',
          icon: 'clock' as const,
          hint: 'Off the queue until next week',
          onClick: () => void send(snoozeChase(chase.account, inDays(7))),
        },
        {
          label: 'Drop it',
          icon: 'waiting' as const,
          // Dropping asks for a reason, and that form lives on the row.
          hint: 'Asks you why, on the row',
          onClick: () => setDropping(true),
        },
      ],
    };
  };

  return (
    <li className="mo-chase" data-tier={chase.tier}>
      <div className="mo-chase-what">
        <button type="button" className="mo-chase-open" onClick={() => setOpen(true)}>
          <p className="mo-chase-account">{chase.account}</p>
          <p className="mo-chase-line">{chase.line}</p>
        </button>
        {/*
          A REAL name or the note saying why there is none. Never an address: the recipient here is
          what a greeting gets built from, and "Dear jenny.ng@..." is worse than no greeting.
        */}
        <p className="mo-chase-who">
          {chase.recipient !== null ? `Write to ${chase.recipient}` : chase.recipientNote}
        </p>
      </div>

      {open && <DeckDetail item={detail()} onClose={() => setOpen(false)} />}

      <div className="mo-chase-actions">
        <button
          type="button"
          onClick={() => openAsk({ lane: 'zou', kind: 'follow-up-email', about: chase.account })}
        >
          <Icon name="action" size={13} />
          {chase.draft?.body != null ? 'Redraft' : 'Draft'}
        </button>
        {/* A week is the common case; the endpoint takes any ISO date if that changes. */}
        <button type="button" onClick={() => void send(snoozeChase(chase.account, inDays(7)))}>
          <Icon name="clock" size={13} />
          Snooze a week
        </button>
        <button type="button" onClick={() => setDropping(true)}>
          <Icon name="waiting" size={13} />
          Drop
        </button>
      </div>

      {dropping && (
        <form
          className="mo-chase-drop"
          onSubmit={(e) => {
            e.preventDefault();
            // Guarded here as well as by the disabled button, because a form submits on Enter.
            if (reason.trim() === '') return;
            void send(dropChase(chase.account, reason.trim()));
          }}
        >
          <label htmlFor={`why-${chase.account}`}>Why are you dropping it?</label>
          <input
            id={`why-${chase.account}`}
            // Focused on open: pressing Drop reveals this form rather than dropping, so the cursor
            // has to land here or the second press looks like the first one did nothing.
            autoFocus
            value={reason}
            placeholder="shown back to you beside the row"
            onChange={(e) => setReason(e.target.value)}
          />
          <button type="submit" disabled={reason.trim() === ''}>
            Drop it
          </button>
        </form>
      )}

      {chase.draft !== undefined && <ChaseDraftBlock account={chase.account} draft={chase.draft} />}

      {error !== null && <p className="mo-chase-error">{error}</p>}
    </li>
  );
}

/**
 * The drafted follow-up, or the reason there is not one.
 *
 * COPY, NOT SEND, and the button says so. It is the only action here on purpose: every path that
 * would put this in front of a counterparty runs through his own mailbox.
 */
function ChaseDraftBlock({ account, draft }: { account: string; draft: DashboardChaseDraft }): JSX.Element {
  const [copied, setCopied] = useState(false);

  /*
   * A REFUSAL IS SHOWN, NOT HIDDEN. Rendering nothing here would be indistinguishable from the pass
   * not having run, and it would leave him wondering whether the feature was broken. The sentence
   * says what MO OS looked at and why it declined.
   */
  if (draft.body === null) {
    return (
      <div className="mo-chase-draft" data-empty="true">
        <p className="mo-chase-draft-why">{draft.why ?? 'No follow-up was drafted for this account.'}</p>
        {draft.proposal !== null && <ChaseProposal account={account} proposal={draft.proposal} />}
      </div>
    );
  }

  const copy = () => {
    /*
     * Guarded rather than assumed. `navigator.clipboard` is undefined without a secure context and
     * in the test environment, and an unguarded call throws out of the click handler, which on this
     * row would read as the button doing nothing at all.
     */
    void navigator.clipboard?.writeText(`Subject: ${draft.subject ?? ''}\n\n${draft.body ?? ''}`);
    setCopied(true);
  };

  return (
    <div className="mo-chase-draft">
      <p className="mo-chase-draft-head">
        {/* Where the specifics came from, because the two are worth different amounts of trust. */}
        {draft.grounding === 'web'
          ? 'Drafted from a web lookup, since nobody here has ever replied'
          : 'Drafted from what MO OS has read'}
        <span>{draft.at.slice(0, 10)}</span>
      </p>
      {draft.subject !== null && draft.subject !== '' && (
        <p className="mo-chase-draft-subject">{draft.subject}</p>
      )}
      <p className="mo-chase-draft-body">{draft.body}</p>

      {draft.sources.length > 0 && (
        <ul className="mo-chase-draft-sources">
          {draft.sources.map((s) => (
            <li key={s.url}>
              {/* Shown so the hook can be CHECKED. An unverifiable claim is the failure this lane
                  exists to avoid, and a link he can open is what makes it verifiable. */}
              <a href={s.url} target="_blank" rel="noreferrer noopener">
                {s.title === '' ? s.url : s.title}
              </a>
            </li>
          ))}
        </ul>
      )}
      {draft.citations.length > 0 && (
        <p className="mo-chase-draft-cites">
          Rests on {draft.citations.map((c) => c.replace(/^.*\//, '').replace(/\.md$/, '')).join(', ')}
        </p>
      )}
      {draft.caveats.map((c) => (
        <p className="mo-chase-draft-caveat" key={c}>
          {c}
        </p>
      ))}

      {draft.proposal !== null && <ChaseProposal account={account} proposal={draft.proposal} />}

      <button type="button" className="mo-chase-draft-copy" onClick={copy}>
        <Icon name="action" size={13} />
        {copied ? 'Copied, now send it yourself' : 'Copy the email'}
      </button>
    </div>
  );
}

/**
 * The stage change, as a QUESTION.
 *
 * Rendered exactly the way `Pipeline.tsx` renders `proposedStage`, and for the reason it states:
 * a proposal shown as though it were the state is the fabrication the rest of the deck refuses.
 * Nothing here can apply it, and nothing on the server does either.
 */
function ChaseProposal({
  account,
  proposal,
}: {
  account: string;
  proposal: { stage: string; why: string };
}): JSX.Element {
  return (
    <p className="mo-chase-draft-proposal">
      Move {account} to <strong>{proposal.stage}</strong>? {proposal.why}
    </p>
  );
}
